import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  compose,
  setDisplayName,
} from 'recompose';
import moment from 'moment';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/core/styles';

import immutableToJS from 'helpers/immutableToJS';
import { renderDateTime } from 'helpers/date-time';
import {
  injectIntl,
  SmartDialog,
} from 'components';
import { broadcastActions } from 'redux/mails/actions';
import { selectState } from 'redux/selectors';

const styles = () => ({
  modalContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  notes: {
    color: 'red',
    marginBottom: 5,
  },
  tableRow: {
    cursor: 'pointer',
    transition: '0.5s',
    '&:hover': {
      background: 'lightgray',
    },
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
  },
});

const options = [7, 14].map(d => (
  <MenuItem
    key={d}
    value={d}
  >
    Last {d} days
  </MenuItem>),
);

function getDuration(dateRange) {
  const now = moment();
  const dateTo = now.format();
  const dateFrom = now.subtract(dateRange, 'days').format();

  return { dateFrom, dateTo };
}

class BroadcastStatsDialog extends Component {
  static propTypes = {
    broadcast: PropTypes.shape({
      subject: PropTypes.string,
      messages: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          email: PropTypes.string.isRequired,
          opens: PropTypes.number.isRequired,
          ts: PropTypes.number.isRequired,
        }),
      ),
    }).isRequired,
    broadcastError: PropTypes.shape({}).isRequired,
    broadcastRequesting: PropTypes.bool.isRequired,
    broadcasts: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        recipient: PropTypes.string.isRequired,
        subject: PropTypes.string.isRequired,
        createdAt: PropTypes.string.isRequired,
      }),
    ).isRequired,
    broadcastsError: PropTypes.shape({}).isRequired,
    broadcastsRequesting: PropTypes.bool.isRequired,
    classes: PropTypes.shape({}).isRequired,
    isVendor: PropTypes.bool.isRequired,
    open: PropTypes.bool.isRequired,

    formatMessage: PropTypes.func.isRequired,
    listBroadcasts: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    readBroadcast: PropTypes.func.isRequired,
  };

  state = {
    dateRange: 7,
    isDetailsView: false,
  };

  componentDidMount() {
    this.loadData();
  }

  handleBroadcastRowClick = (broadcast) => {
    const { dateRange } = this.state;
    const { dateFrom, dateTo } = getDuration(dateRange);

    this.setState({ isDetailsView: true });

    this.props.readBroadcast(broadcast.id, { dateFrom, dateTo });
  }

  handleDateRangeChange = (ev) => {
    this.setState({ dateRange: ev.target.value }, this.loadData);
  }

  handleGoBack = () => {
    this.setState({ isDetailsView: false });
  }

  loadData = () => {
    const { listBroadcasts, isVendor } = this.props;
    const { dateRange } = this.state;
    const { dateFrom, dateTo } = getDuration(dateRange);

    listBroadcasts({ isVendor, dateFrom, dateTo });
  }

  renderBroadcastStats = () => {
    const {
      broadcasts,
      broadcastsRequesting,
      broadcastsError,
      classes,
      formatMessage,
    } = this.props;

    if (broadcastsRequesting) {
      return <CircularProgress className="loader" />;
    }

    if (Object.keys(broadcastsError).length > 0) {
      return (
        <span>
          {formatMessage('There was an error listing broadcasts')}
        </span>
      );
    }

    return (
      <Fragment>
        <span className={classes.notes}>
          {formatMessage('Notes: It might take up to a few hours for email open status to be updated.')}
        </span>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{formatMessage('Subject')}</TableCell>
              <TableCell>{formatMessage('Recipient')}</TableCell>
              <TableCell>{formatMessage('Date')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {broadcasts.map(broadcast => (
              <TableRow
                key={broadcast.id}
                className={classes.tableRow}
                onClick={() => this.handleBroadcastRowClick(broadcast)}
              >
                <TableCell>{broadcast.subject}</TableCell>
                <TableCell>{broadcast.recipient || formatMessage('All')}</TableCell>
                <TableCell>{renderDateTime(moment(broadcast.createdAt))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Fragment>
    );
  };

  renderBroadcastDetails = () => {
    const {
      broadcast,
      broadcastRequesting,
      broadcastError,
      formatMessage,
    } = this.props;

    if (broadcastRequesting) {
      return <CircularProgress className="loader" />;
    }

    if (Object.keys(broadcastError).length > 0) {
      return (
        <span>
          {formatMessage('There was an error reading the broadcast details')}
        </span>
      );
    }

    const numberOfOpenedMessages = broadcast.messages.filter(m => m.opens > 0).length;
    const numberOfMessages = broadcast.messages.length;

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{formatMessage('To')}</TableCell>
            <TableCell><span style={{ fontWeight: 700, color: 'black' }}>{`${numberOfOpenedMessages} / ${numberOfMessages} ${formatMessage('Opened')}`}</span></TableCell>
            <TableCell>{formatMessage('Date')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {broadcast.messages.map(m => (
            <TableRow key={m.id}>
              <TableCell>{m.email}</TableCell>
              <TableCell>{m.opens > 0 ? formatMessage('Yes') : formatMessage('No')}</TableCell>
              <TableCell>{renderDateTime(moment(m.ts * 1000))}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  renderTitle = () => {
    const {
      broadcast,
      broadcastRequesting,
      classes,
      formatMessage,
    } = this.props;
    const { dateRange, isDetailsView } = this.state;

    if (isDetailsView) {
      return (
        <div className={classes.title}>
          <span>
            {broadcastRequesting ? formatMessage('Broadcast Details') : broadcast.subject}
          </span>
          <Button
            onClick={this.handleGoBack}
          >
            {'<'}
          </Button>
        </div>
      );
    }

    return (
      <div className={classes.title}>
        <span>{formatMessage('Broadcast Stats')}</span>
        <Select
          onChange={this.handleDateRangeChange}
          value={dateRange}
        >
          {options}
        </Select>
      </div>
    );
  };

  render() {
    const {
      classes,
      open,
      onClose,
    } = this.props;
    const { isDetailsView } = this.state;

    return (
      <SmartDialog
        maxWidth="lg"
        onClose={onClose}
        open={open}
        title={this.renderTitle()}
      >
        <div className={classes.modalContent}>
          { isDetailsView ? this.renderBroadcastDetails() : this.renderBroadcastStats() }
        </div>
      </SmartDialog>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = state => ({
  ...selectState('mails', 'broadcast')(state, 'broadcast'),
  ...selectState('mails', 'broadcasts')(state, 'broadcasts'),
});

const mapDispatchToProps = dispatch => ({
  listBroadcasts: (...args) =>
    dispatch(broadcastActions.list(...args)),
  readBroadcast: (...args) =>
    dispatch(broadcastActions.read(...args)),
});

export default compose(
  setDisplayName('BroadcastStatsDialog'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(styles),
  immutableToJS,
  injectIntl,
)(BroadcastStatsDialog);
